import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "grid grid-cols-1 md:grid-cols-3 gap-12 items-stretch" }
const _hoisted_2 = { class: "md:absolute md:inset-0 flex flex-col gap-4 md:gap-0 md:justify-evenly" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "font-bold text-lg" }
const _hoisted_5 = { class: "overflow-hidden" }
const _hoisted_6 = { class: "text-gray-600 dark:text-gray-400 text-sm pt-2" }
const _hoisted_7 = {
  key: 0,
  class: "absolute bottom-0 left-0 w-full h-1 bg-gray-200 dark:bg-gray-800"
}
const _hoisted_8 = { class: "relative w-full h-full flex items-center justify-center rounded-lg overflow-visible md:col-span-2 min-h-[300px] md:min-h-[600px] max-h-[600px]" }
const _hoisted_9 = ["src", "loop"]
const _hoisted_10 = {
  key: 0,
  class: "flex h-20 w-20 items-center justify-center rounded-full border-2 border-primary-400 bg-primary-500/80 backdrop-blur-sm dark:border-primary-600"
}

import { nextTick, onUnmounted, ref, watch } from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'VideoFeatureCard',
  props: {
  sectionTitle: {
    type: String,
    required: true,
  },
  sectionDescription: {
    type: String,
    required: true,
  },
  features: {
    type: Array as () => {
      title: string;
      description: string;
      video: string;
    }[],
    required: true,
  },
  orientation: {
    type: String as () => "left" | "right",
    default: "right",
  },
  mini: {
    type: Boolean,
    default: false,
  },
  loopAll: {
    type: Boolean,
    default: true,
  },
},
  setup(__props) {

const props = __props;

const selectedFeature = ref(props.features[0]);
const videoRef = ref<HTMLVideoElement | null>(null);
const playbackProgress = ref(0);
const animationFrameId = ref<number | null>(null);
const isPaused = ref(!props.loopAll);
const videoOpacity = ref(1);

function togglePlayPause() {
  if (videoRef.value) {
    if (videoRef.value.paused) {
      videoRef.value.play();
    } else {
      videoRef.value.pause();
    }
  }
}

function selectFeature(
  feature: { title: string; description: string; video: string },
  isLooping: boolean = false,
) {
  if (selectedFeature.value.title === feature.title) {
    togglePlayPause();
    return;
  }

  if (isLooping && !videoRef.value?.paused) {
    // Don't set isPaused to true when looping
  } else {
    isPaused.value = true;
  }

  videoOpacity.value = 0;
  setTimeout(() => {
    selectedFeature.value = feature;
    playbackProgress.value = 0;
  }, 300);
}

function updateProgressLoop() {
  if (videoRef.value?.duration) {
    playbackProgress.value = (videoRef.value.currentTime / videoRef.value.duration) * 100;
  }
  animationFrameId.value = requestAnimationFrame(updateProgressLoop);
}

function handlePlay() {
  isPaused.value = false;
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value);
  }
  animationFrameId.value = requestAnimationFrame(updateProgressLoop);
}

function handlePause() {
  isPaused.value = true;
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value);
    animationFrameId.value = null;
  }
}

function playNextVideo() {
  if (!props.loopAll) return;

  const currentIndex = props.features.findIndex((f) => f.title === selectedFeature.value.title);
  const nextIndex = (currentIndex + 1) % props.features.length;
  selectFeature(props.features[nextIndex], true);
}

watch(videoRef, (newVideoEl, oldVideoEl) => {
  if (oldVideoEl) {
    oldVideoEl.removeEventListener("play", handlePlay);
    oldVideoEl.removeEventListener("pause", handlePause);
    oldVideoEl.removeEventListener("ended", playNextVideo);
  }
  if (newVideoEl) {
    newVideoEl.addEventListener("play", handlePlay);
    newVideoEl.addEventListener("pause", handlePause);
    newVideoEl.addEventListener("ended", playNextVideo);
    isPaused.value = newVideoEl.paused;
    nextTick(() => {
      videoOpacity.value = 1;
    });
  }
});

onUnmounted(() => {
  if (animationFrameId.value) {
    cancelAnimationFrame(animationFrameId.value);
  }
  if (videoRef.value) {
    videoRef.value.removeEventListener("play", handlePlay);
    videoRef.value.removeEventListener("pause", handlePause);
    videoRef.value.removeEventListener("ended", playNextVideo);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_UIcon = _resolveComponent("UIcon")!
  const _component_UPageSection = _resolveComponent("UPageSection")!

  return (_openBlock(), _createBlock(_component_UPageSection, {
    title: __props.sectionTitle,
    description: __props.sectionDescription
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          class: _normalizeClass([{ 'md:order-last': __props.orientation === 'left' }, "relative md:col-span-1"])
        }, [
          _createElementVNode("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.features, (feature) => {
              return (_openBlock(), _createElementBlock("div", {
                key: feature.title,
                class: _normalizeClass(["p-4 rounded-lg cursor-pointer transition-all duration-300 border relative overflow-hidden", {
              'bg-primary-50 dark:bg-primary-950 border-primary-500 shadow-lg':
                selectedFeature.value.title === feature.title,
              'border-gray-200 dark:border-gray-800 hover:border-primary-400/50 hover:bg-gray-50 dark:hover:bg-gray-800/50':
                selectedFeature.value.title !== feature.title,
            }]),
                onClick: ($event: any) => (selectFeature(feature))
              }, [
                _createElementVNode("h4", _hoisted_4, _toDisplayString(feature.title), 1),
                _createElementVNode("div", {
                  class: _normalizeClass(["grid transition-[grid-template-rows] duration-300 ease-in-out", [
                // Base (mobile): only show description for the currently selected card
                selectedFeature.value.title === feature.title ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                // md and up: if not mini, show all descriptions
                !__props.mini && 'md:grid-rows-[1fr]',
              ]])
                }, [
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("p", _hoisted_6, _toDisplayString(feature.description), 1)
                  ])
                ], 2),
                (selectedFeature.value.title === feature.title)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createElementVNode("div", {
                        class: "h-1 bg-primary-500 transition-all duration-100 ease-linear",
                        style: _normalizeStyle({ width: `${playbackProgress.value}%` })
                      }, null, 4)
                    ]))
                  : _createCommentVNode("", true)
              ], 10, _hoisted_3))
            }), 128))
          ])
        ], 2),
        _createElementVNode("div", _hoisted_8, [
          (_openBlock(), _createElementBlock("video", {
            ref_key: "videoRef",
            ref: videoRef,
            key: selectedFeature.value.video,
            src: selectedFeature.value.video,
            autoplay: "",
            loop: !__props.loopAll,
            muted: "",
            playsinline: "",
            class: "rounded-lg shadow-2xl w-full transition-opacity duration-300",
            style: _normalizeStyle({ opacity: videoOpacity.value })
          }, null, 12, _hoisted_9)),
          _createElementVNode("div", {
            class: "absolute inset-0 flex cursor-pointer items-center justify-center",
            onClick: togglePlayPause
          }, [
            (isPaused.value && !__props.loopAll)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createVNode(_component_UIcon, {
                    name: "i-heroicons-play-solid",
                    class: "h-10 w-10 text-white"
                  })
                ]))
              : _createCommentVNode("", true)
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["title", "description"]))
}
}

})